import { useState } from "react"

import { SwapHoriz } from '@mui/icons-material'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
} from '@mui/material'

import { usersService, type User } from "../../api"
import { ROLE_OPTIONS_WITH_DESCRIPTION as ROLE_OPTIONS, type UserRole } from "../../constants/roles"

interface ChangeRoleDialogProps {
  user: User
  onRoleChanged?: () => void
}

export function ChangeRoleDialog({ user, onRoleChanged }: ChangeRoleDialogProps) {
  const [isChanging, setIsChanging] = useState(false)
  const [open, setOpen] = useState(false)
  const [selectedRole, setSelectedRole] = useState<UserRole>(user.role)
  const [error, setError] = useState<string | null>(null)

  const handleOpen = () => {
    setSelectedRole(user.role)
    setError(null)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setError(null)
  }

  const handleChangeRole = async () => {
    if (selectedRole === user.role) {
      setError('Please select a different role')
      return
    }

    try {
      setIsChanging(true)
      setError(null)
      await usersService.updateUser(user.id, { role: selectedRole })
      setOpen(false)
      if (onRoleChanged) {
        onRoleChanged()
      }
    } catch (err) {
      console.error('Failed to change user role:', err)
      setError('Failed to change user role. Please try again.')
    } finally {
      setIsChanging(false)
    }
  }

  const selectedRoleOption = ROLE_OPTIONS.find(r => r.value === selectedRole)

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<SwapHoriz />}
        onClick={(e) => {
          e.stopPropagation()
          handleOpen()
        }}
        sx={{
          fontSize: '0.75rem',
          fontWeight: 500,
          minWidth: 'auto',
          px: 1.5,
          py: 0.5,
          height: 28,
        }}
      >
        Change Role
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Change User Role
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            User: <strong>{user.email}</strong>
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <FormControl fullWidth>
            <InputLabel id="role-select-label">Role</InputLabel>
            <Select
              labelId="role-select-label"
              value={selectedRole}
              label="Role"
              onChange={(e) => setSelectedRole(e.target.value as UserRole)}
            >
              {ROLE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      {option.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedRoleOption && selectedRole !== user.role && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Current role: <strong>{user.role.toUpperCase()}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                New role: <strong>{selectedRoleOption.label.toUpperCase()}</strong>
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} variant="outlined" disabled={isChanging}>
            Cancel
          </Button>
          <Button
            onClick={handleChangeRole}
            variant="contained"
            disabled={isChanging || selectedRole === user.role}
          >
            {isChanging ? 'Changing...' : 'Change Role'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
